// ═══════════════════════════════════════════════════════════════════
//  Sheet-Link helpers — type-safe access to BlockConfig.linkTarget
// ═══════════════════════════════════════════════════════════════════
//
//  BlockConfig has an index signature `[key: string]: string | undefined`
//  to be JSON-serializable. We store linkTarget as a pipe-delimited
//  string and provide get/set helpers for type-safe usage.
//
//  Format: "documentId|sheetId|blockId"
//          (sheetId and blockId optional — empty if absent)
//
// ═══════════════════════════════════════════════════════════════════

import type { BlockConfig, WorkflowBlock, MultiSheetWorkflowState } from '@/types/workflow';
import type { SheetLinkTarget, CrossDocumentRef } from '@/types/atlas';
import type { AtlasAnswerCitation } from '@/hooks/useExplainAtlas';

/** Map an AI citation to a SheetLinkTarget for navigation. */
export function citationToSheetLinkTarget(c: AtlasAnswerCitation): SheetLinkTarget {
  return {
    documentId: c.documentId,
    sheetId: c.sheetId,
    blockId: c.blockId,
  };
}

/** Encode a SheetLinkTarget into the string format used by BlockConfig.linkTarget. */
export function encodeLinkTarget(target: SheetLinkTarget): string {
  return [target.documentId, target.sheetId ?? '', target.blockId ?? ''].join('|');
}

/** Decode a BlockConfig.linkTarget string. Returns null if invalid. */
export function decodeLinkTarget(raw: string | undefined): SheetLinkTarget | null {
  if (!raw) return null;
  const parts = raw.split('|');
  if (parts.length < 1 || !parts[0]) return null;
  const target: SheetLinkTarget = { documentId: parts[0] };
  if (parts[1]) target.sheetId = parts[1];
  if (parts[2]) target.blockId = parts[2];
  return target;
}

/** Get the SheetLinkTarget from a BlockConfig, or null if not set. */
export function getLinkTarget(config: BlockConfig): SheetLinkTarget | null {
  return decodeLinkTarget(config.linkTarget);
}

/** Set the SheetLinkTarget on a BlockConfig. Returns a new config object. */
export function setLinkTarget(
  config: BlockConfig,
  target: SheetLinkTarget | null,
): BlockConfig {
  if (target == null) {
    const { linkTarget: _, ...rest } = config;
    return rest as BlockConfig;
  }
  return { ...config, linkTarget: encodeLinkTarget(target) };
}

/** Type guard: does this block have a sheet-link target? */
export function isSheetLinkBlock(block: WorkflowBlock): boolean {
  return block.type === 'sheet-link' || getLinkTarget(block.config) != null;
}

/** Create a sheet-link block at the given position. */
export function createSheetLinkBlock(opts: {
  id: string;
  x: number;
  y: number;
  name: string;
  target: SheetLinkTarget;
  description?: string;
  color?: string;
}): WorkflowBlock {
  return {
    id: opts.id,
    type: 'sheet-link',
    x: opts.x,
    y: opts.y,
    color: opts.color ?? 'hsl(217 91% 60%)',
    config: {
      name: opts.name,
      description: opts.description,
      icon: 'external-link',
      linkTarget: encodeLinkTarget(opts.target),
    },
  };
}

/**
 * Walk a document's sheets and emit CrossDocumentRefs for every
 * sheet-link block. Used by the manifest generator + indexer.
 */
export function deriveRefsFromSheetLinks(
  documentId: string,
  workflow: MultiSheetWorkflowState,
  uuid: () => string = () => crypto.randomUUID(),
): CrossDocumentRef[] {
  const refs: CrossDocumentRef[] = [];
  for (const sheet of workflow.sheets) {
    for (const block of sheet.blocks) {
      const target = getLinkTarget(block.config);
      if (!target) continue;
      refs.push({
        id: uuid(),
        fromDocumentId: documentId,
        fromSheetId: sheet.id,
        fromBlockId: block.id,
        toDocumentId: target.documentId,
        toSheetId: target.sheetId,
        toBlockId: target.blockId,
        type: 'see-also',
        label: block.config.name,
      });
    }
  }
  return refs;
}
